import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CreanceRow } from "@/lib/creance.api";
import { getTotalDue } from "../creance-calculations";
import {
  formatDate,
  formatMoney,
  getCreanceInvoiceLabel,
  parseAmount,
} from "../creance-formatters";
import { CreanceStatusBadge } from "./CreanceStatusBadge";

interface CreanceGridProps {
  rows: CreanceRow[];
  onSelect: (row: CreanceRow) => void;
}

export function CreanceGrid({ rows, onSelect }: CreanceGridProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-8 text-center text-[12px] text-muted-foreground">
        Aucune créance.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {rows.map((row) => (
        <div key={row.installmentId} className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold text-foreground">{row.partyName}</p>
              <p className="mt-0.5 text-[10.5px] text-muted-foreground">
                {getCreanceInvoiceLabel(row)} - échéance #{row.sequence}
              </p>
            </div>
            <CreanceStatusBadge creance={row} />
          </div>

          <p className="mt-3 text-[20px] font-bold tabular-nums text-foreground">
            {formatMoney(getTotalDue(row))}
          </p>

          <div className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-3 text-[11px]">
            <div>
              <p className="text-subtle">Date prévue</p>
              <p className="mt-0.5 font-medium text-body">{formatDate(row.expectedDate)}</p>
            </div>
            <div>
              <p className="text-subtle">Pénalité</p>
              <p className="mt-0.5 font-medium text-body">
                {formatMoney(parseAmount(row.penaltyDue))}
              </p>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-3">
            <Link
              to={`/invoices/${row.invoiceId}`}
              className="text-[11.5px] font-medium text-brand-gold-dark hover:underline"
            >
              Voir facture
            </Link>
            <Button size="sm" variant="ghost" onClick={() => onSelect(row)}>
              <Eye size={13} /> Détail
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
